import cafeteriasService from "@/store/services/cafeterias-service";
import {
  OPERATION_TYPE_PRODUCTION,
  OPERATION_TYPE_SATELLITE,
} from "@/constants/cafeteria";

/**
 * Eligibility rules of the internal orders, resolved from the cafeterias of each
 * establishment:
 *
 *  - an establishment may PLACE an internal order only if it hosts a cafeteria of
 *    type SATELLITE;
 *  - an establishment may RECEIVE an internal order (be the production side) only
 *    if it hosts a cafeteria of type PRODUCTION.
 *
 * These rules live in the management app only — deliberately no server-side check,
 * so a special case can always be handled by hand in the API.
 *
 * The cafeterias service is called directly rather than through the shared vuex
 * module: several of these lookups run side by side (the selector, the satellite
 * check, the production-cafeteria prefill) and would otherwise overwrite each
 * other's `cafeterias/list` state.
 */

const PAGE_SIZE = 999;

function fetchCafeteriasByOperationType({
  operationType,
  organizationId = null,
  establishmentId = null,
  include = "establishment",
}) {
  return cafeteriasService
    .list({
      filter: {
        operation_type: operationType,
        ...(organizationId ? { organization: organizationId } : {}),
        ...(establishmentId ? { establishment: establishmentId } : {}),
      },
      include,
      page: { number: 1, size: PAGE_SIZE },
    })
    .then(({ list }) => list || []);
}

/**
 * Distinct establishments hosting at least one cafeteria of the given type.
 */
async function fetchEstablishmentsByCafeteriaType({
  operationType,
  organizationId = null,
}) {
  const cafeterias = await fetchCafeteriasByOperationType({
    operationType,
    organizationId,
  });

  const establishments = {};
  cafeterias.forEach((cafeteria) => {
    const establishment = cafeteria.establishment;
    if (!establishment || !establishment.id) return;
    establishments[establishment.id] = establishment;
  });

  return Object.values(establishments).sort((a, b) =>
    (a.company_name || "").localeCompare(b.company_name || "")
  );
}

/**
 * Establishments that may be the production side of an internal order.
 */
export function fetchProductionEstablishments({ organizationId = null } = {}) {
  return fetchEstablishmentsByCafeteriaType({
    operationType: OPERATION_TYPE_PRODUCTION,
    organizationId,
  });
}

/**
 * Does that establishment host a cafeteria of the given operation type?
 * Returns null when it could not be determined (request failure), so callers can
 * tell "no" apart from "unknown" and stay non-blocking.
 */
async function establishmentHasCafeteriaType({ establishmentId, operationType }) {
  if (!establishmentId) return null;

  try {
    const cafeterias = await fetchCafeteriasByOperationType({
      operationType,
      establishmentId,
    });

    return cafeterias.some(
      (cafeteria) =>
        cafeteria.operation_type === operationType &&
        (!cafeteria.establishment || cafeteria.establishment.id === establishmentId)
    );
  } catch (error) {
    return null;
  }
}

/**
 * May that establishment place internal orders? (needs a SATELLITE cafeteria)
 */
export function establishmentCanOrder(establishmentId) {
  return establishmentHasCafeteriaType({
    establishmentId,
    operationType: OPERATION_TYPE_SATELLITE,
  });
}

/**
 * May that establishment be the production side? (needs a PRODUCTION cafeteria)
 */
export function establishmentCanProduce(establishmentId) {
  return establishmentHasCafeteriaType({
    establishmentId,
    operationType: OPERATION_TYPE_PRODUCTION,
  });
}

/**
 * The satellite cafeteria of an establishment together with its production
 * cafeteria, used to prefill the production side of a new order.
 * Returns null when the establishment declares none.
 */
export async function findSatelliteCafeteriaWithProduction(establishmentId) {
  if (!establishmentId) return null;

  try {
    const cafeterias = await fetchCafeteriasByOperationType({
      operationType: OPERATION_TYPE_SATELLITE,
      establishmentId,
      include: "productionCafeteria,productionCafeteria.establishment",
    });

    return cafeterias.find((cafeteria) => cafeteria.productionCafeteria) || null;
  } catch (error) {
    return null;
  }
}
